import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Building,
  Mail,
  MapPin,
  Edit,
  Trash2,
  Star,
  ArrowRight,
  Scale,
  Cake,
  ShieldCheck,
  MessageCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface Client {
  id: string;
  name: string;
  email: string;
  phone?: string;
  mobile?: string;
  cpf_cnpj?: string;
  client_type: 'person' | 'company';
  birth_date?: string;
  city?: string;
  state?: string;
  legal_areas?: string[];
  client_priority?: 'low' | 'medium' | 'high' | 'vip';
  confidentiality_level?: 'normal' | 'high' | 'confidential';
  created_at: string;
  [key: string]: any;
}

interface ClientCardProps {
  client: Client;
  onEdit: (client: Client) => void;
  onDelete: (id: string) => void;
  onView: (client: Client) => void;
  isBirthday?: boolean;
  processCount?: number;
  /** Mostra a data de aniversário em destaque (aba "Aniversários do mês") */
  showBirthDate?: boolean;
}

/** Dia e mês do aniversário, sem deslocar o dia por fuso */
export const birthdayInfo = (birthDate?: string) => {
  if (!birthDate) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthDate);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const now = new Date();
  const today = now.getDate();
  const sameMonth = month === now.getMonth() + 1;
  const turning = now.getFullYear() - year;
  const label = new Date(2000, month - 1, day).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' });
  let when = '';
  if (sameMonth) {
    if (day === today) when = 'hoje';
    else if (day === today + 1) when = 'amanhã';
    else if (day > today) when = `em ${day - today} dias`;
    else when = 'já passou';
  }
  return { day, month, label, turning, when };
};

export const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const calculateAge = (birthDate?: string) => {
  if (!birthDate) return null;
  const birth = birthDate.includes('T') ? new Date(birthDate) : new Date(`${birthDate}T12:00:00`);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

/** Prioridade de tramitação prevista em lei (idoso 60+, 80+, criança/adolescente) */
export const getLegalPriority = (client: Pick<Client, 'birth_date' | 'client_type'>) => {
  if (client.client_type === 'company') return null;
  const age = calculateAge(client.birth_date);
  if (age === null) return null;
  if (age >= 80) return { label: 'Prioridade 80+', strong: true };
  if (age >= 60) return { label: 'Prioridade idoso', strong: false };
  if (age <= 14) return { label: 'Prioridade criança', strong: false };
  return null;
};

export const getClientPhone = (client: Pick<Client, 'mobile' | 'phone'>) => client.mobile || client.phone || '';

export const whatsappLink = (phone: string, text?: string) => {
  const digits = phone.replace(/\D/g, '');
  const base = `https://wa.me/${digits.startsWith('55') ? digits : `55${digits}`}`;
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
};

/** Primeiro nome com só a inicial maiúscula (ex.: "MARIA DA PAZ" -> "Maria") */
const firstName = (name: string) => {
  const first = (name || '').trim().split(/\s+/)[0] || '';
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
};

/** Mensagem de aniversário enviada pelo WhatsApp */
export const birthdayMessage = (name: string) =>
  `Olá, ${firstName(name)}! 🎉\n\n` +
  `Hoje é o seu dia, e eu não poderia deixar passar. ` +
  `Desejo um novo ano cheio de saúde, paz e boas conquistas.\n\n` +
  `Conte sempre comigo!\n` +
  `Um abraço,\nAllana Sthéfany`;

export const ClientCard = ({ client, onEdit, onDelete, onView, isBirthday = false, processCount = 0, showBirthDate = false }: ClientCardProps) => {
  const birthday = showBirthDate ? birthdayInfo(client.birth_date) : null;
  const phone = getClientPhone(client);
  const legalPriority = getLegalPriority(client);
  const isVip = client.client_priority === 'vip';
  const location = [client.city, client.state].filter(Boolean).join('/');
  const documentLabel = client.cpf_cnpj ? `${client.client_type === 'company' ? 'CNPJ' : 'CPF'} ${client.cpf_cnpj}` : null;

  return (
    <Card
      className={cn(
        'group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-none transition-all duration-300 hover:-translate-y-px hover:border-[#c4ab83]/60 hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)]',
        isBirthday && 'border-pink-200'
      )}
    >
      <CardContent className="p-4">
        {/* Identificação */}
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => onView(client)}
            className={cn(
              'relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white',
              client.client_type === 'company'
                ? 'bg-gradient-to-br from-slate-600 to-slate-800'
                : 'bg-gradient-to-br from-[#c4ab83] to-[#9a7f52]'
            )}
            title="Ver detalhes"
          >
            {client.client_type === 'company' ? <Building className="h-5 w-5" /> : getInitials(client.name)}
            {isBirthday && (
              <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-pink-500 ring-2 ring-white" title="Aniversariante do dia">
                <Cake className="h-3 w-3" />
              </span>
            )}
          </button>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <button type="button" onClick={() => onView(client)} className="min-w-0 text-left">
                <h3 className="truncate text-[15px] font-semibold leading-tight text-slate-900 transition-colors hover:text-[#9a7f52]" title={client.name}>
                  {client.name}
                </h3>
              </button>
              {isVip && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                  <Star className="h-3 w-3 fill-amber-500 text-amber-500" /> VIP
                </span>
              )}
            </div>
            <p className="mt-0.5 truncate text-xs text-slate-500">
              {[documentLabel, location].filter(Boolean).join(' · ') || 'Sem documento ou cidade'}
            </p>
            {(legalPriority || isBirthday) && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {legalPriority && (
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                      legalPriority.strong ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
                    )}
                  >
                    <ShieldCheck className="h-3 w-3" /> {legalPriority.label}
                  </span>
                )}
                {isBirthday && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-pink-50 px-2 py-0.5 text-[10px] font-semibold text-pink-700">
                    <Cake className="h-3 w-3" /> Aniversário hoje
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {birthday && (
          <div
            className={cn(
              'mt-3 flex items-center gap-3 rounded-xl px-3 py-2',
              birthday.when === 'já passou' ? 'bg-slate-50' : 'bg-pink-50/70'
            )}
          >
            <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-lg bg-white ring-1 ring-pink-100">
              <span className="text-base font-semibold leading-none tabular-nums text-slate-900">{String(birthday.day).padStart(2, '0')}</span>
              <span className="mt-0.5 text-[9px] font-semibold uppercase leading-none text-pink-600">
                {new Date(2000, birthday.month - 1, 1).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '')}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800">
                <Cake className="mr-1 inline h-3.5 w-3.5 text-pink-500" />
                {birthday.label}
              </p>
              <p className="text-xs text-slate-500">
                {birthday.turning > 0 && birthday.turning < 130 ? `${birthday.when === 'já passou' ? 'Fez' : 'Faz'} ${birthday.turning} anos` : 'Aniversário'}
                {birthday.when && ` · ${birthday.when}`}
              </p>
            </div>
          </div>
        )}

        {isBirthday && phone && (
          <a
            href={whatsappLink(phone, birthdayMessage(client.name))}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-pink-50 to-[#faf7f2] py-2 text-[13px] font-semibold text-pink-700 ring-1 ring-pink-100 transition-colors hover:from-pink-100 hover:to-pink-50"
            title="Abre o WhatsApp com a mensagem pronta"
          >
            <Cake className="h-4 w-4" />
            Enviar parabéns no WhatsApp
          </a>
        )}

        {/* Contato e ações */}
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
          <div className="flex min-w-0 items-center gap-1.5">
            {phone ? (
              <a
                href={whatsappLink(phone)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
                title={`WhatsApp ${phone}`}
              >
                <MessageCircle className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{phone}</span>
                <span className="sm:hidden">WhatsApp</span>
              </a>
            ) : (
              <span className="text-xs italic text-slate-400">Sem telefone</span>
            )}
            {client.email && (
              <a
                href={`mailto:${client.email}`}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                title={client.email}
              >
                <Mail className="h-4 w-4" />
              </a>
            )}
            <span
              className={cn(
                'ml-1 inline-flex items-center gap-1 text-xs',
                processCount ? 'font-medium text-slate-600' : 'text-slate-400'
              )}
              title="Processos vinculados"
            >
              <Scale className="h-3.5 w-3.5" />
              {processCount} {processCount === 1 ? 'processo' : 'processos'}
            </span>
          </div>

          <div className="flex shrink-0 items-center gap-0.5">
            <div className="flex items-center gap-0.5 transition-opacity lg:opacity-0 lg:group-hover:opacity-100">
              <Button variant="ghost" size="icon" onClick={() => onEdit(client)} className="h-8 w-8 text-slate-500 hover:bg-amber-50 hover:text-amber-700" title="Editar">
                <Edit className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => onDelete(client.id)} className="h-8 w-8 text-slate-500 hover:bg-red-50 hover:text-red-600" title="Excluir">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onView(client)}
              className="h-8 rounded-lg px-2 text-xs font-semibold text-[#7a6440] hover:bg-[#c4ab83]/10 hover:text-[#5c4a2e]"
            >
              Abrir
              <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
